import type { FocusItem } from './types';
import type { WindowAggregate } from './aggregateOrders';
import { formatMoney, formatPct, pctChange } from './format';

export type FocusInput = {
  connectionId: string;
  connectionStatus: string;
  yesterday: WindowAggregate;
  prior: WindowAggregate;
  inventoryZero: number | null;
  inventorySample: number;
  inventoryTotal: number | null;
  ordersDomainQuality?: string;
  ordersLastSync?: string;
  ordersError?: string;
  syncStaleHours: number | null;
  ordersScanTruncated?: boolean;
  ordersHrefBase: string;
  inventoryHrefBase: string;
  connectHref: string;
};

/**
 * Deterministic focus list — max 10. No AI.
 * Severity: critical > warning > opportunity.
 */
export function buildFocusItems(input: FocusInput): FocusItem[] {
  const items: FocusItem[] = [];
  const y = input.yesterday;
  const p = input.prior;
  const gmvPct = pctChange(y.sales, p.sales);
  const ordersPct = pctChange(y.orderCount, p.orderCount);
  const cancelRate = y.orderCount >= 20 ? y.canceled / y.orderCount : null;
  const unshippedRate = y.orderCount > 0 ? y.unshipped / y.orderCount : null;

  if (
    input.connectionStatus === 'error' ||
    input.connectionStatus === 'disconnected' ||
    input.connectionStatus === 'failed'
  ) {
    items.push({
      id: 'conn-bad',
      severity: 'critical',
      title: 'Connection not healthy',
      evidence: `Status is ${input.connectionStatus}.`,
      href: input.connectHref,
    });
  }

  if (input.ordersError) {
    items.push({
      id: 'orders-fetch-fail',
      severity: 'critical',
      title: 'Orders data failed to load',
      evidence: input.ordersError,
      href: input.ordersHrefBase,
    });
  }

  if (input.ordersDomainQuality && /fail|error|dead/i.test(input.ordersDomainQuality)) {
    items.push({
      id: 'orders-quality',
      severity: 'critical',
      title: 'Orders domain quality issue',
      evidence: `Sync quality: ${input.ordersDomainQuality}.`,
      href: input.connectHref,
    });
  }

  if (input.syncStaleHours != null && input.syncStaleHours > 24) {
    items.push({
      id: 'sync-stale',
      severity: 'critical',
      title: 'Orders sync is stale',
      evidence: `Last successful orders sync about ${Math.round(input.syncStaleHours)} hours ago.`,
      href: input.connectHref,
    });
  }

  if (gmvPct != null && gmvPct <= -40) {
    items.push({
      id: 'gmv-critical',
      severity: 'critical',
      title: 'Sales dropped sharply vs prior day',
      evidence: `GMV ${formatPct(gmvPct)} (${formatMoney(y.sales, y.currencies[0])} vs prior).`,
      href: `${input.ordersHrefBase}`,
    });
  } else if (gmvPct != null && gmvPct <= -20) {
    items.push({
      id: 'gmv-warn',
      severity: 'warning',
      title: 'Sales down vs prior day',
      evidence: `GMV ${formatPct(gmvPct)}.`,
      href: input.ordersHrefBase,
    });
  } else if (gmvPct != null && gmvPct >= 15) {
    items.push({
      id: 'gmv-up',
      severity: 'opportunity',
      title: 'Sales up vs prior day',
      evidence: `GMV ${formatPct(gmvPct)} (${formatMoney(y.sales, y.currencies[0])}).`,
      href: input.ordersHrefBase,
    });
  }

  if (ordersPct != null && ordersPct <= -50) {
    items.push({
      id: 'orders-critical',
      severity: 'critical',
      title: 'Order count drop is severe',
      evidence: `Orders ${formatPct(ordersPct)} (${y.orderCount} vs ${p.orderCount}).`,
      href: input.ordersHrefBase,
    });
  } else if (ordersPct != null && ordersPct <= -30) {
    items.push({
      id: 'orders-warn',
      severity: 'warning',
      title: 'Fewer orders than prior day',
      evidence: `Orders ${formatPct(ordersPct)} (${y.orderCount} vs ${p.orderCount}).`,
      href: input.ordersHrefBase,
    });
  } else if (ordersPct != null && ordersPct >= 20) {
    items.push({
      id: 'orders-up',
      severity: 'opportunity',
      title: 'Order volume increased',
      evidence: `Orders ${formatPct(ordersPct)} (${y.orderCount} vs ${p.orderCount}).`,
      href: input.ordersHrefBase,
    });
  }

  if (unshippedRate != null && y.unshipped >= 5 && unshippedRate >= 0.25) {
    items.push({
      id: 'unshipped',
      severity: unshippedRate >= 0.4 ? 'critical' : 'warning',
      title: 'High share of open orders yesterday',
      evidence: `${y.unshipped} of ${y.orderCount} orders in open Amazon statuses.`,
      href: `${input.ordersHrefBase}&status=Unshipped`,
    });
  }

  if (cancelRate != null && cancelRate >= 0.15) {
    items.push({
      id: 'cancel-rate',
      severity: cancelRate >= 0.25 ? 'critical' : 'warning',
      title: 'Elevated cancellation rate',
      evidence: `${y.canceled}/${y.orderCount} canceled yesterday (${(cancelRate * 100).toFixed(1)}%).`,
      href: `${input.ordersHrefBase}&status=Canceled`,
    });
  }

  if (
    input.inventoryZero != null &&
    input.inventorySample > 0 &&
    input.inventoryZero / input.inventorySample >= 0.1
  ) {
    items.push({
      id: 'zero-stock',
      severity: input.inventoryZero / input.inventorySample >= 0.25 ? 'critical' : 'warning',
      title: 'Many SKUs show zero fulfillable stock',
      evidence: `${input.inventoryZero} of ${input.inventorySample} sampled inventory rows at zero (store total ${input.inventoryTotal ?? '—'}).`,
      href: input.inventoryHrefBase,
    });
  }

  if (y.orderCount === 0 && p.orderCount === 0 && !input.ordersError) {
    items.push({
      id: 'no-orders-window',
      severity: 'warning',
      title: 'No orders in yesterday window',
      evidence: 'Among scanned MarketSync order headers, none fall on yesterday (local calendar).',
      href: input.ordersHrefBase,
    });
  }

  if (y.sales == null && y.orderCount > 0) {
    items.push({
      id: 'gmv-missing',
      severity: 'warning',
      title: 'Sales total incomplete',
      evidence:
        y.currencies.length > 1
          ? 'Multiple currencies in window — GMV not mixed.'
          : `Only ${y.ordersWithTotal}/${y.orderCount} headers have OrderTotal.`,
      href: input.ordersHrefBase,
    });
  }

  if (input.ordersScanTruncated) {
    items.push({
      id: 'scan-truncated',
      severity: 'warning',
      title: 'Brief order scan truncated',
      evidence: 'Brief scanned a capped page window of LIVE headers. Numbers cover recent pages only; confidence is reduced.',
      href: input.ordersHrefBase,
    });
  }

  const rank = { critical: 0, warning: 1, opportunity: 2 } as const;
  return items
    .sort((a, b) => rank[a.severity] - rank[b.severity])
    .slice(0, 10);
}
